import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Tab } from 'semantic-ui-react';
import {
  FilterGroup,
  FilterGroupItem,
  FilterGroupItemSubCategory,
} from '../../../interfaces/POSGroupFilter';

import './GroupFilter.less';
import {
  OrderBy,
  POSIcon,
  POSLoadingIndicator,
  SearchPanel,
} from '../../../index';
import { POSCheckButton } from '../../POSCheckButton/POSCheckButton';
import { SELECT_ALL } from '../POSGroupFilterConstants';

export interface Props {
  filterItems?: FilterGroupItem[];
  filterGroup: FilterGroup;
  selectedFilterItems: FilterGroupItem[];
  tabPaneLoadingText?: string;
  onFilterChange?: (filterItem: FilterGroupItem, checked: boolean) => void;
}

export const GroupFilterTabPane: FC<Props> = ({
  filterItems,
  filterGroup,
  tabPaneLoadingText,
  onFilterChange,
  selectedFilterItems,
}) => {
  const [filteredFilterItems, setFilteredFilterItems] = useState(
    filterItems ?? []
  );
  const [searchString, setSearchString] = useState('');

  const [selectedAccordionIndex, setSelectedAccordionIndex] = useState<
    number[]
  >([]);

  const handleSearch = (value: string) => {
    setSearchString(value);
  };

  useEffect(() => {
    if (searchString.length == 0) {
      setFilteredFilterItems([...(filterItems ?? [])]);
      return;
    }
    const filters = [...(filterItems ?? [])];

    const searchedItems = filters.filter((el) =>
      el.itemName.toLowerCase().includes(searchString.toLowerCase())
    );

    const filteredItems: FilterGroupItem[] = [];

    searchedItems.forEach((searchedItem) => {
      if (searchedItem.parentId) {
        const parent = filters.find(
          (item) => item.itemId == searchedItem.parentId
        );
        parent && filteredItems.push(parent);

        const siblings = filters.filter(
          (item) => item.parentId == searchedItem.parentId
        );
        filteredItems.push(...siblings);
      } else {
        filteredItems.push(searchedItem);
      }
    });

    const distinctItems = [...new Set(filteredItems)];
    setFilteredFilterItems(distinctItems);
  }, [searchString, filterItems]);

  useEffect(() => {
    setSearchString('');
    setSelectedAccordionIndex([]);
  }, [filterGroup]);

  const onExpandIconClick = useCallback((index: number) => {
    setSelectedAccordionIndex((prev) => {
      if (prev.indexOf(index) >= 0) {
        return [...prev.filter((x) => x !== index)];
      }
      return [...prev, index];
    });
  }, []);

  const getCheckedItems = useCallback(
    (item: FilterGroupItem) =>
      selectedFilterItems.findIndex((y) => y.itemId === item.itemId) >= 0,
    [selectedFilterItems]
  );

  const allParentItems = useMemo(() => {
    return filteredFilterItems.filter((item) => item.parentId == null);
  }, [filteredFilterItems]);

  const parentIdsWithChild = useMemo(() => {
    const allChildItems = filteredFilterItems.filter(
      (item) => item.parentId != null
    );
    return allChildItems?.map((c) => c.parentId);
  }, [filteredFilterItems]);

  const parentsWithChildren = useMemo(() => {
    return allParentItems.filter((p) => parentIdsWithChild.includes(p.itemId));
  }, [allParentItems, parentIdsWithChild]);

  const parentsWithoutChildItems = useMemo(() => {
    return allParentItems.filter((p) => !parentIdsWithChild.includes(p.itemId));
  }, [allParentItems, parentIdsWithChild]);

  const renderItemsColumnWise = useCallback(
    (childItems: FilterGroupItem[]) => {
      return (
        <div className="column-items">
          {childItems.map((x) => (
            <>
              <POSCheckButton
                key={x.itemId}
                checked={getCheckedItems(x)}
                onToggle={(checked) => onFilterChange?.(x, checked)}
              >
                {x.itemName}
              </POSCheckButton>
            </>
          ))}
        </div>
      );
    },
    [getCheckedItems, onFilterChange]
  );

  const renderSubCategories = useCallback(
    (parentId: string) => {
      const childItems = filteredFilterItems.filter(
        (item) => item.parentId == parentId
      );
      const subCategories: FilterGroupItemSubCategory[] = [];

      childItems.forEach((childItem) => {
        if (
          subCategories.findIndex(
            (sub) => sub.subCategoryId === childItem.subCategory?.subCategoryId
          ) < 0
        ) {
          childItem.subCategory && subCategories.push(childItem.subCategory);
        }
      });

      const sortedSubCategories = OrderBy(
        subCategories,
        (x: FilterGroupItemSubCategory) => x.subCategoryName
      );

      return sortedSubCategories.map((subCategory, index) => {
        const childItemsBySubcateGory = childItems.filter(
          (item) =>
            item.subCategory?.subCategoryId === subCategory.subCategoryId
        );
        return (
          <div
            key={subCategory.subCategoryId}
            className={`sub-category ${
              index != subCategories.length - 1 && 'divider-bottom'
            }`}
          >
            <span>{subCategory.subCategoryName}</span>
            {renderItemsColumnWise(childItemsBySubcateGory)}
          </div>
        );
      });
    },
    [filteredFilterItems, renderItemsColumnWise]
  );

  const renderSelectAll = useCallback(() => {
    return (
      <div className="divider-bottom">
        <POSCheckButton
          checked={selectedFilterItems.length == filterItems?.length}
          checkedItemsLength={
            selectedFilterItems.length > 0 &&
            filterItems &&
            selectedFilterItems.length < filterItems.length
              ? 1
              : 0
          }
          onToggle={(checked) => {
            onFilterChange?.(
              { itemId: SELECT_ALL, itemName: '', groupId: '' },
              checked
            );
          }}
        >
          Select All
        </POSCheckButton>
      </div>
    );
  }, [filterItems, onFilterChange, selectedFilterItems.length]);

  const renderParentsWithChild = useCallback(() => {
    return parentsWithChildren.map((parent, i) => {
      return (
        <div key={parent.itemId} className="parent divider-bottom">
          <POSCheckButton
            key={parent.itemId}
            checked={getCheckedItems(parent)}
            onToggle={(checked) => onFilterChange?.(parent, checked)}
          >
            {parent.itemName}
          </POSCheckButton>
          {selectedAccordionIndex.indexOf(i) >= 0 ? (
            <POSIcon
              icon="arrow-up"
              className="header-icon"
              onClick={() => onExpandIconClick(i)}
            />
          ) : (
            <POSIcon
              icon="arrow-down"
              className="header-icon"
              onClick={() => onExpandIconClick(i)}
            />
          )}
          {selectedAccordionIndex.indexOf(i) >= 0 &&
            renderSubCategories(parent.itemId)}
        </div>
      );
    });
  }, [
    parentsWithChildren,
    getCheckedItems,
    selectedAccordionIndex,
    renderSubCategories,
    onFilterChange,
    onExpandIconClick,
  ]);

  const renderParentsWithoutChildItems = useCallback(() => {
    return renderItemsColumnWise(parentsWithoutChildItems);
  }, [parentsWithoutChildItems, renderItemsColumnWise]);

  const getLoadingText = useCallback(() => {
    if (tabPaneLoadingText) {
      return tabPaneLoadingText;
    } else if (filterItems && filteredFilterItems.length == 0) {
      return (
        <>
          Sorry, we were not able to find a match with your search.
          <br />
          <b>Please check your spelling</b> OR <b>try another search.</b>
        </>
      );
    }
    return '';
  }, [filterItems, filteredFilterItems.length, tabPaneLoadingText]);

  const renderLoadingIcon = useCallback(() => {
    return (
      <div className="empty-pane">
        <POSLoadingIndicator />
        {getLoadingText()}
      </div>
    );
  }, [getLoadingText]);

  return (
    <Tab.Pane>
      <SearchPanel
        delay={500}
        fluid
        submitAlways
        loading={false}
        onSubmit={handleSearch}
        placeHolder={`Search ${filterGroup.groupName}`}
        searchString={searchString}
      />

      {filteredFilterItems.length > 0 ? (
        <>
          {renderSelectAll()}
          <div className="scrollable">
            {renderParentsWithChild()}
            {renderParentsWithoutChildItems()}
          </div>
        </>
      ) : (
        renderLoadingIcon()
      )}
    </Tab.Pane>
  );
};
